import { BrightcoveVideo } from "@components/Blocks/BrightcoveVideo/BrightcoveVideo";
import { KAKI_MAKAN_AUTOPLAY_PLAYER_ID } from "@config/kakiMakan";
import { type LatestVideoResponse } from "@transformer/useBrightcoveResponse";
import { RefObject } from "react";
import { twMerge } from "tailwind-merge";

type Props = {
  playing: LatestVideoResponse;
  forwadedRef: RefObject<HTMLDivElement>;
  onEnded?: () => void;
  nextPlaylist?: () => void;
  variant?: "video_page" | "kaki_makan_page" | "article_infinite_scroll";
  showDescription?: boolean;
  propAccountId?: string;
};

export default function VideoPlayerSticky({
  playing,
  forwadedRef,
  onEnded,
  nextPlaylist,
  variant = "video_page",
  showDescription = false,
  propAccountId,
}: Props) {
  const { id, name, topStoryDate, description, published_at, webinar_time } =
    playing;

  const isKakiMakan = variant === "kaki_makan_page";
  const isArticleInfiniteScroll = variant === "article_infinite_scroll";

  const dateToShow = isKakiMakan
    ? `${published_at} | ${webinar_time}`
    : topStoryDate;

  const wrapperClass = isKakiMakan
    ? "w-full max-w-[334px] p-0 gap-[13px] lg:gap-2 lg:px-0 lg:pb-5 lg:pt-0"
    : isArticleInfiniteScroll
      ? "gap-5 lg:flex-row w-full"
      : "gap-[10px] lg:flex-row lg:max-w-screen-lg xl:max-w-screen-cxl border-b-2 border-grey-100 px-[10px] lg:px-[5px] pb-[18px] pt-[17px] lg:pt-[20px]";

  const playerStyle = isKakiMakan ? "!aspect-[167/297]" : "";

  const innerWrapperClass = isKakiMakan
    ? "hidden lg:flex w-full gap-2"
    : isArticleInfiniteScroll
      ? "flex-grow flex-shrink-0 basis-0 gap-3"
      : "lg:w-[28%]";

  const titleClass = isKakiMakan
    ? "leading-[120%] lg:leading-[130%]"
    : "leading-[130%]";

  const descriptionClass = isKakiMakan
    ? "text-sm leading-[150%]"
    : `${!isArticleInfiniteScroll && "my-2"} text-xxs lg:text-xs`;

  const dateClass = isKakiMakan ? "text-sm leading-[150%]" : "lg:text-xs";

  const applyAutoPlayer = isKakiMakan || isArticleInfiniteScroll;
  const playerId = applyAutoPlayer && KAKI_MAKAN_AUTOPLAY_PLAYER_ID;

  const playerClass = isKakiMakan
    ? "w-full"
    : isArticleInfiniteScroll
      ? "w-full lg:w-[600px]"
      : "lg:w-[72%]";

  return (
    <div
      className={`flex flex-col mx-auto sticky top-[40px] lg:top-[119px] z-10 bg-bg-primary ${wrapperClass}`}
    >
      <div className={playerClass}>
        <BrightcoveVideo
          videoId={id}
          forwardedRef={forwadedRef}
          onEnded={onEnded}
          nextPlaylist={nextPlaylist}
          propAccountId={propAccountId}
          playerStyle={playerStyle}
          playerId={playerId || ""}
        />
      </div>
      <div className={`flex flex-col ${innerWrapperClass}`}>
        <h3
          className={`font-medium font-secondary text-[22px] text-black-800 ${titleClass}`}
        >
          {name}
        </h3>
        {showDescription && (
          <span
            className={`hidden lg:block font-[500] text-grey-350 ${descriptionClass}`}
          >
            {description}
          </span>
        )}
        <div className={twMerge("flex items-center justify-between")}>
          <span className={`font-[500] text-text-muted ${dateClass}`}>
            {dateToShow}
          </span>
        </div>
      </div>
    </div>
  );
}
